import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { IconStore } from "../../components/layout/icons";
import { CkEditorField } from "../../components/ui/CkEditorField";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../components/ui/Feedback";
import { ExcelExportButton } from "../../components/ui/ExcelExportButton";
import { SearchableSelect } from "../../components/ui/SearchableSelect";
import { SortableTh } from "../../components/ui/SortableTh";
import { ApiError } from "../../lib/api-client";
import { confirmUpdate } from "../../lib/confirm-service";
import { exportRowsToExcel, fetchAllPages } from "../../lib/excel-export";
import { stripHtml } from "../../lib/rich-text";
import { useTableSort } from "../../lib/table-sort";
import { useToast } from "../../lib/toast";
import { fetchStoreRows } from "./store.api";
import {
  useCreateStoreRecord,
  usePatchStoreRecord,
  useStoreIssue,
  useStoreLookup,
  useStoreRows,
} from "./store.queries";
import "./store.css";

const humanize = (key: string) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

export type StoreReturnKind = "store" | "vendor" | "repair";
type StoreReturnMode = "quantity" | "serial";
type StoreReturnLineDraft = {
  key: string;
  issue: string;
  issueLine: string;
  unitQuantity: string;
  subUnitQuantity: string;
  note: string;
};
type VendorReturnLineDraft = {
  key: string;
  batch: string;
  unitQuantity: string;
  subUnitQuantity: string;
  note: string;
};

const RETURN_SORTABLE_COLUMNS = [
  "returnNumber",
  "returnDate",
  "type",
  "department",
  "vendor",
  "status",
  "repairStatus",
  "repairFrom",
  "repairAmount",
  "approvedAt",
] as const;

const details: Record<
  StoreReturnKind,
  { title: string; description: string; submit: string }
> = {
  store: {
    title: "Return to Store",
    description: "Receive issued stock back into the original store batch.",
    submit: "Receive return",
  },
  vendor: {
    title: "Return to Vendor",
    description: "Record stock returned to an approved supplier.",
    submit: "Record vendor return",
  },
  repair: {
    title: "Repair / Damage Items",
    description:
      "Send issued stock for repair, then return it to the original batch when work is complete.",
    submit: "Send for repair",
  },
};

const today = () => new Date().toISOString().slice(0, 10);
const initialReturnForm = (kind: StoreReturnKind) => ({
  returnDate: today(),
  department: "",
  issue: "",
  issueLine: "",
  vendor: "",
  item: "",
  batch: "",
  unitQuantity: "0",
  subUnitQuantity: "0",
  disposition: kind === "repair" ? "repair" : "return",
  note: "",
  remarks: "",
  approvalNote: "",
});
const newStoreReturnLine = (): StoreReturnLineDraft => ({
  key: crypto.randomUUID(),
  issue: "",
  issueLine: "",
  unitQuantity: "0",
  subUnitQuantity: "0",
  note: "",
});
const newVendorReturnLine = (): VendorReturnLineDraft => ({
  key: crypto.randomUUID(),
  batch: "",
  unitQuantity: "0",
  subUnitQuantity: "0",
  note: "",
});
const scalarText = (value: unknown): string => {
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return stripHtml(value);
  return "-";
};
const text = (value: unknown): string => {
  if (value === null || value === undefined) return "-";
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const scalar = [record.name, record.vendorName, record.id].find(
      (entry) => typeof entry === "string" || typeof entry === "number",
    );
    return scalar !== undefined ? String(scalar) : "-";
  }
  return scalarText(value);
};
const lineIdText = (value: unknown): string =>
  typeof value === "string" || typeof value === "number" ? String(value) : "";
const rowLines = (row: Record<string, unknown>): Record<string, unknown>[] =>
  Array.isArray(row.lines)
    ? row.lines.filter(
        (line): line is Record<string, unknown> =>
          typeof line === "object" && line !== null,
      )
    : [];
const repairDate = (value: unknown): string => {
  if (!value) return "-";
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};
const repairQuantity = (line: Record<string, unknown>): string =>
  `${text(line.unitQuantity)} PCS ${text(line.subUnitQuantity)} PCS`;

export const StoreReturnPage = ({ kind }: { kind: StoreReturnKind }) => {
  const meta = details[kind];
  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(() => initialReturnForm(kind));
  const [repairForm, setRepairForm] = useState({
    repairId: "",
    repairFrom: "",
    repairTimeDays: "0",
    repairAmount: "0",
    repairBillReference: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [showRepairForm, setShowRepairForm] = useState(false);
  const [returnItemsFound, setReturnItemsFound] = useState(false);
  const [returnMode, setReturnMode] = useState<StoreReturnMode>("quantity");
  const [storeReturnLines, setStoreReturnLines] = useState<
    StoreReturnLineDraft[]
  >(() => [newStoreReturnLine()]);
  const [vendorItemsFound, setVendorItemsFound] = useState(false);
  const [vendorReturnLines, setVendorReturnLines] = useState<
    VendorReturnLineDraft[]
  >(() => [newVendorReturnLine()]);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const { showToast } = useToast();
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
  });

  const listParams: Record<string, unknown> = sortBy
    ? { returnType: kind, sortBy, sortOrder, page: 1 }
    : { returnType: kind };
  const returns = useStoreRows("returns", true, listParams);
  const departments = useStoreLookup("masters", { kind: "department" });
  const issues = useStoreLookup(
    "issues",
    { department: form.department },
    kind === "repair" && Boolean(form.department),
  );
  const returnLookupType =
    returnMode === "serial"
      ? "serial-returnable-issue-lines"
      : "returnable-issue-lines";
  const returnableIssueLines = useStoreLookup(
    returnLookupType,
    { department: form.department },
    kind === "store" &&
      showForm &&
      returnItemsFound &&
      Boolean(form.department),
  );
  const vendors = useStoreLookup("vendors");
  const items = useStoreLookup("items");
  const vendorBatches = useStoreLookup(
    "batches",
    { item: form.item },
    kind === "vendor" && Boolean(form.item),
  );
  const vendorReturnBatches = useStoreLookup(
    "vendor-return-batches",
    { vendor: form.vendor },
    kind === "vendor" && showForm && vendorItemsFound && Boolean(form.vendor),
  );
  const issue = useStoreIssue(kind === "repair" ? form.issue : "");
  const createReturn = useCreateStoreRecord();
  const completeRepair = usePatchStoreRecord();

  const issueLines = (issue.data?.lines ?? []) as Record<string, unknown>[];
  const availableLines = useMemo(
    () => issueLines.filter((line) => Number(line.remainingQuantity ?? 0) > 0),
    [issueLines],
  );
  const selectedLine = issueLines.find(
    (line) => lineIdText(line.id) === form.issueLine,
  );
  const selectedReturnLine = returnableIssueLines.data?.find(
    (line) => line.id === form.issueLine,
  );
  const selectedStoreReturnLines = storeReturnLines.map(
    (line) =>
      returnableIssueLines.data?.find((entry) => entry.id === line.issueLine) ??
      null,
  );
  const selectedVendorReturnBatches = vendorReturnLines.map(
    (line) =>
      vendorReturnBatches.data?.find((entry) => entry.id === line.batch) ??
      null,
  );
  const selectedItem = items.data?.find(
    (item) =>
      item.id ===
      (kind === "vendor" ? form.item : lineIdText(selectedLine?.item)),
  );
  const selectedBatch = vendorBatches.data?.find(
    (batch) => batch.id === form.batch,
  );
  const selectedSubUnitsPerUnit = Number(
    (kind === "store"
      ? selectedReturnLine?.subUnitsPerUnit
      : selectedItem?.subUnitsPerUnit) ?? 1,
  );
  const storeReturnQuantity = storeReturnLines.reduce((sum, line, index) => {
    const source = selectedStoreReturnLines[index];
    if (!source) return sum;
    if (returnMode === "serial") return sum + 1;
    return (
      sum +
      Number(line.unitQuantity || 0) * Number(source.subUnitsPerUnit ?? 1) +
      Number(line.subUnitQuantity || 0)
    );
  }, 0);
  const vendorReturnQuantity = vendorReturnLines.reduce((sum, line, index) => {
    const source = selectedVendorReturnBatches[index];
    if (!source) return sum;
    return (
      sum +
      Number(line.unitQuantity || 0) * Number(source.subUnitsPerUnit ?? 1) +
      Number(line.subUnitQuantity || 0)
    );
  }, 0);
  const returnQuantity =
    kind === "store"
      ? storeReturnQuantity
      : kind === "vendor"
        ? vendorReturnQuantity
        : Number(form.unitQuantity || 0) * selectedSubUnitsPerUnit +
          Number(form.subUnitQuantity || 0);
  const returnRate =
    kind === "vendor"
      ? Number(selectedBatch?.rate ?? 0)
      : kind === "store"
        ? Number(selectedReturnLine?.rate ?? 0)
        : Number(selectedLine?.rate ?? 0);
  const vendorReturnAmount = vendorReturnLines.reduce((sum, line, index) => {
    const source = selectedVendorReturnBatches[index];
    if (!source) return sum;
    const quantity =
      Number(line.unitQuantity || 0) * Number(source.subUnitsPerUnit ?? 1) +
      Number(line.subUnitQuantity || 0);
    return (
      sum +
      (quantity / Number(source.subUnitsPerUnit ?? 1)) *
        Number(source.rate ?? 0)
    );
  }, 0);
  const returnAmount =
    kind === "vendor"
      ? vendorReturnAmount
      : (returnQuantity / selectedSubUnitsPerUnit) * returnRate;
  const columns = useMemo(
    () =>
      Object.keys(returns.data?.data[0] ?? {}).filter(
        (key) =>
          ![
            "_id",
            "id",
            "lines",
            "createdAt",
            "updatedAt",
            "returnedBy",
            "repairCompletedBy",
          ].includes(key),
      ),
    [returns.data],
  );

  useEffect(() => {
    setForm(initialReturnForm(kind));
    setRepairForm({
      repairId: "",
      repairFrom: "",
      repairTimeDays: "0",
      repairAmount: "0",
      repairBillReference: "",
    });
    setShowForm(false);
    setShowRepairForm(false);
    setReturnItemsFound(false);
    setReturnMode("quantity");
    setStoreReturnLines([newStoreReturnLine()]);
    setVendorItemsFound(false);
    setVendorReturnLines([newVendorReturnLine()]);
    setSortBy(null);
    setSortOrder("asc");
  }, [kind]);

  useEffect(() => {
    const state = location.state as {
      openReturnForm?: boolean;
      returnMode?: StoreReturnMode;
    } | null;
    if (!state?.openReturnForm) return;
    setReturnMode(state.returnMode ?? "quantity");
    setShowRepairForm(false);
    setShowForm(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const exportToExcel = async () => {
    const rows = await fetchAllPages<Record<string, unknown>>(
      (page, limit) =>
        fetchStoreRows("returns", { ...listParams, page, limit }),
      500,
    );
    const exportColumns = Object.keys(rows[0] ?? {}).filter(
      (key) =>
        ![
          "_id",
          "id",
          "lines",
          "createdAt",
          "updatedAt",
          "returnedBy",
          "repairCompletedBy",
        ].includes(key),
    );
    await exportRowsToExcel({
      fileName: `${meta.title.replace(/\s+/g, "-")}-${today()}`,
      title: `${meta.title} History`,
      subtitle: meta.description,
      columns: exportColumns.map((column) => ({
        header: humanize(column),
        width: 16,
        map: (row: Record<string, unknown>) => text(row[column]),
      })),
      rows,
    });
  };

  const openReturnForm = (
    target: "store" | "vendor",
    mode: StoreReturnMode = "quantity",
  ) => {
    const targetPath =
      target === "store"
        ? "/store/returns/to-store"
        : "/store/returns/to-vendor";
    if (kind === target) {
      setShowRepairForm(false);
      setReturnItemsFound(false);
      setReturnMode(mode);
      setShowForm(true);
      return;
    }
    navigate(targetPath, { state: { openReturnForm: true, returnMode: mode } });
  };

  const resetReturnForm = () => {
    setForm(initialReturnForm(kind));
    setReturnItemsFound(false);
    setStoreReturnLines([newStoreReturnLine()]);
    setVendorItemsFound(false);
    setVendorReturnLines([newVendorReturnLine()]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const storeLines = storeReturnLines
      .map((line, index) => ({ line, source: selectedStoreReturnLines[index] }))
      .filter(
        (
          entry,
        ): entry is {
          line: StoreReturnLineDraft;
          source: NonNullable<(typeof selectedStoreReturnLines)[number]>;
        } =>
          Boolean(entry.source) &&
          (returnMode === "serial"
            ? Boolean(entry.line.note.trim())
            : Number(entry.line.unitQuantity || 0) *
                Number(entry.source?.subUnitsPerUnit ?? 1) +
                Number(entry.line.subUnitQuantity || 0) >
              0),
      );
    const vendorLines = vendorReturnLines
      .map((line, index) => ({
        line,
        source: selectedVendorReturnBatches[index],
      }))
      .filter(
        (
          entry,
        ): entry is {
          line: VendorReturnLineDraft;
          source: NonNullable<(typeof selectedVendorReturnBatches)[number]>;
        } =>
          Boolean(entry.source) &&
          Number(entry.line.unitQuantity || 0) *
            Number(entry.source?.subUnitsPerUnit ?? 1) +
            Number(entry.line.subUnitQuantity || 0) >
            0,
      );
    const firstStoreIssue = storeLines[0]?.line.issue ?? "";
    try {
      const created = await createReturn.mutateAsync({
        resource: "returns",
        payload: {
          returnDate: form.returnDate,
          type: kind,
          issue:
            kind === "vendor"
              ? null
              : kind === "store"
                ? firstStoreIssue
                : form.issue,
          vendor: kind === "vendor" ? form.vendor : null,
          lines:
            kind === "store"
              ? storeLines.map(({ line, source }) => ({
                  issueLine: line.issueLine,
                  batch: null,
                  quantity:
                    returnMode === "serial"
                      ? 1
                      : Number(line.unitQuantity || 0) *
                          Number(source.subUnitsPerUnit ?? 1) +
                        Number(line.subUnitQuantity || 0),
                  unitQuantity:
                    returnMode === "serial"
                      ? 1
                      : Number(line.unitQuantity || 0),
                  subUnitQuantity:
                    returnMode === "serial"
                      ? 0
                      : Number(line.subUnitQuantity || 0),
                  disposition: form.disposition,
                  note: line.note || null,
                  reason: null,
                }))
              : kind === "vendor"
                ? vendorLines.map(({ line, source }) => ({
                    issueLine: null,
                    batch: source.batch ?? line.batch,
                    quantity:
                      Number(line.unitQuantity || 0) *
                        Number(source.subUnitsPerUnit ?? 1) +
                      Number(line.subUnitQuantity || 0),
                    unitQuantity: Number(line.unitQuantity || 0),
                    subUnitQuantity: Number(line.subUnitQuantity || 0),
                    disposition: "return",
                    note: line.note || null,
                    reason: null,
                  }))
                : [
                    {
                      issueLine: form.issueLine,
                      batch: null,
                      quantity: returnQuantity,
                      unitQuantity: Number(form.unitQuantity || 0),
                      subUnitQuantity: Number(form.subUnitQuantity || 0),
                      disposition: form.disposition,
                      note: form.note || null,
                      reason: null,
                    },
                  ],
          remarks: form.remarks || null,
        },
      });
      if (kind === "vendor")
        await completeRepair.mutateAsync({
          resource: "returns",
          id: `${String(created.id)}/status`,
          payload: { status: "approved", approvalNote: null },
        });
      showToast("Return saved successfully.", "success");
      resetReturnForm();
      setShowForm(false);
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The return could not be saved.",
        "error",
      );
    }
  };

  const finishRepair = async (event: FormEvent) => {
    event.preventDefault();
    if (!(await confirmUpdate("this repair record"))) return;
    try {
      await completeRepair.mutateAsync({
        resource: "returns",
        id: `${repairForm.repairId}/complete-repair`,
        payload: {
          repairFrom: repairForm.repairFrom,
          repairTimeDays: Number(repairForm.repairTimeDays),
          repairAmount: Number(repairForm.repairAmount),
          repairBillReference: repairForm.repairBillReference || null,
        },
      });
      showToast("Repair completed successfully.", "success");
      setRepairForm({
        repairId: "",
        repairFrom: "",
        repairTimeDays: "0",
        repairAmount: "0",
        repairBillReference: "",
      });
      setShowRepairForm(false);
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The repair could not be completed.",
        "error",
      );
    }
  };

  const updateReturnStatus = async (
    id: string,
    status: "approved" | "rejected",
  ) => {
    if (!(await confirmUpdate(`this return (${status})`))) return;
    try {
      await completeRepair.mutateAsync({
        resource: "returns",
        id: `${id}/status`,
        payload: { status, approvalNote: form.approvalNote || null },
      });
      showToast(
        status === "approved"
          ? "Return approved successfully."
          : "Return rejected successfully.",
        "success",
      );
      setForm((current) => ({ ...current, approvalNote: "" }));
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The return approval could not be saved.",
        "error",
      );
    }
  };

  const firstSelectedStoreIssue =
    storeReturnLines.find((line) => line.issue)?.issue ?? "";
  const selectableReturnLinesFor = (current: StoreReturnLineDraft) =>
    (returnableIssueLines.data ?? []).filter((line) => {
      const sameIssue =
        !firstSelectedStoreIssue ||
        line.issue === firstSelectedStoreIssue ||
        line.issue === current.issue;
      const duplicate = storeReturnLines.some(
        (entry) => entry.key !== current.key && entry.issueLine === line.id,
      );
      return sameIssue && !duplicate;
    });
  const updateStoreReturnLine = (
    key: string,
    values: Partial<StoreReturnLineDraft>,
  ) =>
    setStoreReturnLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...values } : line)),
    );
  const addStoreReturnLine = () =>
    setStoreReturnLines((current) => {
      const firstIssue = current.find((line) => line.issue)?.issue ?? "";
      const maxLines = (returnableIssueLines.data ?? []).filter(
        (line) => !firstIssue || line.issue === firstIssue,
      ).length;
      return current.length < maxLines
        ? [...current, newStoreReturnLine()]
        : current;
    });
  const removeStoreReturnLine = (key: string) =>
    setStoreReturnLines((current) =>
      current.length > 1 ? current.filter((line) => line.key !== key) : current,
    );
  const selectableVendorBatchesFor = (current: VendorReturnLineDraft) =>
    (vendorReturnBatches.data ?? []).filter((batch) => {
      const duplicate = vendorReturnLines.some(
        (entry) => entry.key !== current.key && entry.batch === batch.id,
      );
      return !duplicate;
    });
  const updateVendorReturnLine = (
    key: string,
    values: Partial<VendorReturnLineDraft>,
  ) =>
    setVendorReturnLines((current) =>
      current.map((line) => (line.key === key ? { ...line, ...values } : line)),
    );
  const addVendorReturnLine = () =>
    setVendorReturnLines((current) =>
      current.length < (vendorReturnBatches.data?.length ?? 0)
        ? [...current, newVendorReturnLine()]
        : current,
    );
  const removeVendorReturnLine = (key: string) =>
    setVendorReturnLines((current) =>
      current.length > 1 ? current.filter((line) => line.key !== key) : current,
    );
  const validStoreLines = storeReturnLines.filter((line, index) => {
    const source = selectedStoreReturnLines[index];
    if (!source || !line.issue || !line.issueLine) return false;
    if (returnMode === "serial") return Boolean(line.note.trim());
    return (
      Number(line.unitQuantity || 0) * Number(source.subUnitsPerUnit ?? 1) +
        Number(line.subUnitQuantity || 0) >
      0
    );
  });
  const validVendorLines = vendorReturnLines.filter((line, index) => {
    const source = selectedVendorReturnBatches[index];
    if (!source || !line.batch) return false;
    return (
      Number(line.unitQuantity || 0) * Number(source.subUnitsPerUnit ?? 1) +
        Number(line.subUnitQuantity || 0) >
      0
    );
  });
  const saveDisabled =
    createReturn.isPending ||
    completeRepair.isPending ||
    returnQuantity <= 0 ||
    (kind === "store" &&
      (!form.department ||
        validStoreLines.length === 0 ||
        validStoreLines.length !== storeReturnLines.length)) ||
    (kind === "vendor" &&
      (!form.vendor ||
        validVendorLines.length === 0 ||
        validVendorLines.length !== vendorReturnLines.length));

  return (
    <div className="page-stack store-page">
      <section className="store-return-hero">
        <span aria-hidden="true">
          <IconStore />
        </span>
        <h1>{meta.title}</h1>
      </section>

      {!showForm && !showRepairForm ? (
        <section className="content-card">
          <div className="section-heading">
            <h2>{meta.title} history</h2>
            <div className="table-actions">
              <ExcelExportButton
                onExport={exportToExcel}
                disabled={!returns.data || returns.data.data.length === 0}
              />
              {kind === "vendor" ? (
                <button
                  className="button button-primary"
                  type="button"
                  onClick={() => openReturnForm("vendor")}
                >
                  Return to Vendor
                </button>
              ) : kind === "store" ? (
                <>
                  <button
                    className="button button-primary"
                    type="button"
                    onClick={() => openReturnForm("store", "quantity")}
                  >
                    Return By Quantity
                  </button>
                  <button
                    className="button button-secondary"
                    type="button"
                    onClick={() => openReturnForm("store", "serial")}
                  >
                    Return By Serial No
                  </button>
                </>
              ) : null}
            </div>
          </div>
          {returns.data?.data.some((row) => row.status === "pending") ? (
            <div className="form-grid store-return-approval">
              <label className="field">
                Approval Note
                <input
                  value={form.approvalNote}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      approvalNote: event.target.value,
                    }))
                  }
                  placeholder="Required when rejecting"
                />
              </label>
            </div>
          ) : null}
          {returns.isLoading ? <LoadingState /> : null}
          {returns.isError ? (
            <ErrorState
              title="Unable to load returns"
              message="Please try again."
              action={{
                label: "Try again",
                onClick: () => void returns.refetch(),
              }}
            />
          ) : null}
          {returns.data && returns.data.data.length === 0 ? (
            <EmptyState
              title="No records found"
              message="Use the return buttons to create the first return."
            />
          ) : null}
          {returns.data && returns.data.data.length > 0 ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {columns.map((column) =>
                      (RETURN_SORTABLE_COLUMNS as readonly string[]).includes(
                        column,
                      ) ? (
                        <SortableTh
                          key={column}
                          control={tableSort}
                          column={column}
                        >
                          {humanize(column)}
                        </SortableTh>
                      ) : (
                        <th key={column}>{humanize(column)}</th>
                      ),
                    )}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {returns.data.data.map((row) => (
                    <tr key={String(row.id)}>
                      {columns.map((column) => (
                        <td key={column}>{text(row[column])}</td>
                      ))}
                      <td>
                        {row.status === "pending" ? (
                          <div className="table-actions">
                            <button
                              className="button button-primary"
                              type="button"
                              disabled={completeRepair.isPending}
                              onClick={() =>
                                void updateReturnStatus(
                                  String(row.id),
                                  "approved",
                                )
                              }
                            >
                              Approve
                            </button>
                            <button
                              className="button button-danger"
                              type="button"
                              disabled={
                                completeRepair.isPending ||
                                !form.approvalNote.trim()
                              }
                              onClick={() =>
                                void updateReturnStatus(
                                  String(row.id),
                                  "rejected",
                                )
                              }
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ) : null}

      {showForm ? (
        <form
          className="form-card compact-form"
          onSubmit={(event) => void submit(event)}
        >
          <div className="section-heading">
            <h2>
              {kind === "store"
                ? returnMode === "serial"
                  ? "Serial No Return Processing"
                  : "Quantity Return Processing"
                : kind === "vendor"
                  ? "Vendor Return Processing"
                  : meta.submit}
            </h2>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => {
                resetReturnForm();
                setShowForm(false);
              }}
            >
              Cancel
            </button>
          </div>
          <div className="form-grid">
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field field-sm">
                Return date
                <input
                  type="date"
                  required
                  value={form.returnDate}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      returnDate: event.target.value,
                    }))
                  }
                />
              </label>
            ) : null}
            {kind === "store" ? (
              <Lookup
                label="Department"
                value={form.department}
                options={departments.data}
                onChange={(value) => {
                  setReturnItemsFound(false);
                  setStoreReturnLines([newStoreReturnLine()]);
                  setForm((current) => ({
                    ...current,
                    department: value,
                    issue: "",
                    issueLine: "",
                    unitQuantity: "0",
                    subUnitQuantity: "0",
                  }));
                }}
              />
            ) : null}
            {kind === "store" ? (
              <div className="field field-sm store-return-find-actions">
                <span>&nbsp;</span>
                <div className="table-actions">
                  <button
                    className="button button-primary"
                    type="button"
                    disabled={!form.department}
                    onClick={() => setReturnItemsFound(true)}
                  >
                    Find Items
                  </button>
                  <button
                    className="button button-secondary"
                    type="button"
                    onClick={resetReturnForm}
                  >
                    Reset
                  </button>
                </div>
              </div>
            ) : null}
            {kind === "store" && returnMode === "quantity" ? (
              <p className="field field-full store-return-warning">
                Returnable items should not include NON-CONSUMABLES and
                SEMI-CONSUMABLES.
              </p>
            ) : null}
            {kind === "repair" ? (
              <Lookup
                label="Department"
                value={form.department}
                options={departments.data}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    department: value,
                    issue: "",
                    issueLine: "",
                  }))
                }
              />
            ) : null}
            {kind === "repair" ? (
              <Lookup
                label="Source item issue"
                size="lg"
                value={form.issue}
                options={issues.data}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    issue: value,
                    issueLine: "",
                  }))
                }
              />
            ) : null}
            {kind === "repair" ? (
              <label className="field field-lg">
                Issued item line
                <SearchableSelect
                  value={form.issueLine}
                  disabled={!form.issue || issue.isLoading}
                  placeholder={
                    issue.isLoading
                      ? "Loading issued lines..."
                      : "Select issued item"
                  }
                  clearable={false}
                  options={availableLines.map((line) => ({
                    value: String(line.id),
                    label: `${text(line.itemName)} - ${text(line.batchNumber)} - ${text(line.remainingQuantity)} available to return`,
                  }))}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, issueLine: value }))
                  }
                />
              </label>
            ) : null}
            {kind === "vendor" ? (
              <Lookup
                label="Vendor"
                size="lg"
                value={form.vendor}
                options={vendors.data}
                onChange={(value) => {
                  setVendorItemsFound(false);
                  setVendorReturnLines([newVendorReturnLine()]);
                  setForm((current) => ({
                    ...current,
                    vendor: value,
                    item: "",
                    batch: "",
                    unitQuantity: "0",
                    subUnitQuantity: "0",
                  }));
                }}
              />
            ) : null}
            {kind === "vendor" ? (
              <div className="field field-sm store-return-find-actions">
                <span>&nbsp;</span>
                <div className="table-actions">
                  <button
                    className="button button-primary"
                    type="button"
                    disabled={!form.vendor}
                    onClick={() => setVendorItemsFound(true)}
                  >
                    Find Items
                  </button>
                  <button
                    className="button button-secondary"
                    type="button"
                    onClick={resetReturnForm}
                  >
                    Reset
                  </button>
                </div>
              </div>
            ) : null}
            {false ? (
              <Lookup
                label="Item Name"
                size="lg"
                value={form.item}
                options={items.data}
                onChange={(value) =>
                  setForm((current) => ({ ...current, item: value, batch: "" }))
                }
              />
            ) : null}
            {kind === "repair" ? (
              <label className="field">
                Item Name
                <input disabled value={text(selectedLine?.itemName)} />
              </label>
            ) : null}
            {false ? (
              <Lookup
                label="Batch No"
                size="sm"
                value={form.batch}
                options={vendorBatches.data}
                onChange={(value) =>
                  setForm((current) => ({ ...current, batch: value }))
                }
              />
            ) : null}
            {kind === "repair" ? (
              <label className="field field-sm">
                Batch No
                <input disabled value={text(selectedLine?.batchNumber)} />
              </label>
            ) : null}
            {false ? (
              <label className="field field-xs">
                Available Qty
                <input
                  disabled
                  value={String(selectedBatch?.availableQuantity ?? "")}
                />
              </label>
            ) : null}
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field field-xs">
                Unit Qty
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  required
                  value={form.unitQuantity}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      unitQuantity: event.target.value,
                    }))
                  }
                />
              </label>
            ) : null}
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field field-xs">
                Unit
                <input disabled value={selectedItem?.unitName ?? ""} />
              </label>
            ) : null}
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field field-xs">
                Sub Unit Qty
                <input
                  type="number"
                  min="0"
                  max={
                    selectedItem?.subUnitsPerUnit
                      ? selectedItem.subUnitsPerUnit - 1
                      : undefined
                  }
                  step="0.1"
                  required
                  value={form.subUnitQuantity}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      subUnitQuantity: event.target.value,
                    }))
                  }
                />
              </label>
            ) : null}
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field field-xs">
                Sub Unit
                <input disabled value={selectedItem?.subUnitName ?? ""} />
              </label>
            ) : null}
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field field-xs">
                Rate (Rs)
                <input disabled value={returnRate.toFixed(2)} />
              </label>
            ) : null}
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field">
                Note
                <input
                  value={form.note}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      note: event.target.value,
                    }))
                  }
                />
              </label>
            ) : null}
            {kind !== "store" && kind !== "vendor" ? (
              <label className="field field-full">
                <CkEditorField
                  label="Remarks"
                  value={form.remarks}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, remarks: value }))
                  }
                />
              </label>
            ) : null}
          </div>

          {kind === "store" && returnItemsFound ? (
            <div className="form-grid">
              <label className="field field-sm">
                Date
                <input
                  type="date"
                  required
                  value={form.returnDate}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      returnDate: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field field-full">
                <CkEditorField
                  label="Remarks"
                  value={form.remarks}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, remarks: value }))
                  }
                />
              </label>
            </div>
          ) : null}
          {kind === "vendor" && vendorItemsFound ? (
            <div className="form-grid">
              <label className="field field-sm">
                Date
                <input
                  type="date"
                  required
                  value={form.returnDate}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      returnDate: event.target.value,
                    }))
                  }
                />
              </label>
              <label className="field field-full">
                <CkEditorField
                  label="Remarks"
                  value={form.remarks}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, remarks: value }))
                  }
                />
              </label>
            </div>
          ) : null}

          {kind === "store" && returnItemsFound ? (
            returnMode === "serial" ? (
              <div className="table-scroll">
                <table className="store-return-entry-table">
                  <thead>
                    <tr>
                      <th>Item Name *</th>
                      <th>Issue No</th>
                      <th>Batch No</th>
                      <th>Serial No *</th>
                      <th>Unit</th>
                      <th>Available</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeReturnLines.map((line, index) => {
                      const selected = selectedStoreReturnLines[index];
                      return (
                        <tr key={line.key}>
                          <td>
                            <SearchableSelect
                              value={line.issueLine}
                              placeholder={
                                returnableIssueLines.isLoading
                                  ? "Loading issued items..."
                                  : "Select Issue Item"
                              }
                              clearable={false}
                              options={selectableReturnLinesFor(line).map(
                                (entry) => ({
                                  value: entry.id,
                                  label: entry.label,
                                }),
                              )}
                              onChange={(value) => {
                                const source = returnableIssueLines.data?.find(
                                  (entry) => entry.id === value,
                                );
                                updateStoreReturnLine(line.key, {
                                  issueLine: value,
                                  issue: source?.issue ?? "",
                                  unitQuantity: "1",
                                  subUnitQuantity: "0",
                                });
                              }}
                            />
                          </td>
                          <td>{text(selected?.issueNumber)}</td>
                          <td>{text(selected?.batchNumber)}</td>
                          <td>
                            <input
                              required
                              value={line.note}
                              onChange={(event) =>
                                updateStoreReturnLine(line.key, {
                                  note: event.target.value,
                                })
                              }
                              placeholder="Enter serial no"
                            />
                          </td>
                          <td>
                            <input disabled value={selected?.unitName ?? ""} />
                          </td>
                          <td>{text(selected?.remainingQuantity)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="table-scroll">
                <table className="store-return-entry-table">
                  <thead>
                    <tr>
                      <th>Item Name *</th>
                      <th>Vendor</th>
                      <th>Note</th>
                      <th>Unit Qty *</th>
                      <th>Unit</th>
                      <th>Sub Unit Qty</th>
                      <th>Sub Unit</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {storeReturnLines.map((line, index) => {
                      const selected = selectedStoreReturnLines[index];
                      const isLast = index === storeReturnLines.length - 1;
                      const canAddMore =
                        selectableReturnLinesFor({
                          ...newStoreReturnLine(),
                          issue: firstSelectedStoreIssue,
                        }).length > 0;
                      return (
                        <tr key={line.key}>
                          <td>
                            <SearchableSelect
                              value={line.issueLine}
                              placeholder={
                                returnableIssueLines.isLoading
                                  ? "Loading issued items..."
                                  : "Select Issue Item"
                              }
                              clearable={false}
                              options={selectableReturnLinesFor(line).map(
                                (entry) => ({
                                  value: entry.id,
                                  label: entry.label,
                                }),
                              )}
                              onChange={(value) => {
                                const source = returnableIssueLines.data?.find(
                                  (entry) => entry.id === value,
                                );
                                updateStoreReturnLine(line.key, {
                                  issueLine: value,
                                  issue: source?.issue ?? "",
                                  unitQuantity: "0",
                                  subUnitQuantity: "0",
                                });
                              }}
                            />
                          </td>
                          <td>
                            <input
                              disabled
                              value={selected?.vendorName ?? ""}
                            />
                          </td>
                          <td>
                            <input
                              value={line.note}
                              onChange={(event) =>
                                updateStoreReturnLine(line.key, {
                                  note: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              required
                              value={line.unitQuantity}
                              onChange={(event) =>
                                updateStoreReturnLine(line.key, {
                                  unitQuantity: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <input disabled value={selected?.unitName ?? ""} />
                          </td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              max={
                                selected?.subUnitsPerUnit
                                  ? selected.subUnitsPerUnit - 1
                                  : undefined
                              }
                              step="0.1"
                              value={line.subUnitQuantity}
                              onChange={(event) =>
                                updateStoreReturnLine(line.key, {
                                  subUnitQuantity: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <input
                              disabled
                              value={selected?.subUnitName ?? ""}
                            />
                          </td>
                          <td>
                            <div className="store-row-actions">
                              {storeReturnLines.length > 1 ? (
                                <button
                                  className="store-remove"
                                  type="button"
                                  onClick={() =>
                                    removeStoreReturnLine(line.key)
                                  }
                                  aria-label="Remove return item"
                                >
                                  x
                                </button>
                              ) : null}
                              {isLast && canAddMore ? (
                                <button
                                  className="store-add"
                                  type="button"
                                  onClick={addStoreReturnLine}
                                  aria-label="Add return item"
                                >
                                  +
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : null}
          {kind === "vendor" && vendorItemsFound ? (
            <div className="table-scroll">
              <table className="store-return-entry-table">
                <thead>
                  <tr>
                    <th>Item Name *</th>
                    <th>Note</th>
                    <th>Batch No *</th>
                    <th>Unit Qty *</th>
                    <th>Unit *</th>
                    <th>Sub Unit Qty *</th>
                    <th>Sub Unit *</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorReturnLines.map((line, index) => {
                    const selected = selectedVendorReturnBatches[index];
                    const isLast = index === vendorReturnLines.length - 1;
                    const canAddMore =
                      selectableVendorBatchesFor(newVendorReturnLine()).length >
                      0;
                    return (
                      <tr key={line.key}>
                        <td>
                          <SearchableSelect
                            value={line.batch}
                            placeholder={
                              vendorReturnBatches.isLoading
                                ? "Loading vendor items..."
                                : "Select Issue Item"
                            }
                            clearable={false}
                            options={selectableVendorBatchesFor(line).map(
                              (batch) => ({
                                value: batch.id,
                                label: batch.label,
                              }),
                            )}
                            onChange={(value) =>
                              updateVendorReturnLine(line.key, {
                                batch: value,
                                unitQuantity: "0",
                                subUnitQuantity: "0",
                              })
                            }
                          />
                        </td>
                        <td>
                          <input
                            value={line.note}
                            onChange={(event) =>
                              updateVendorReturnLine(line.key, {
                                note: event.target.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <input disabled value={selected?.batchNumber ?? ""} />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            required
                            value={line.unitQuantity}
                            onChange={(event) =>
                              updateVendorReturnLine(line.key, {
                                unitQuantity: event.target.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <input disabled value={selected?.unitName ?? ""} />
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={
                              selected?.subUnitsPerUnit
                                ? selected.subUnitsPerUnit - 1
                                : undefined
                            }
                            step="0.1"
                            required
                            value={line.subUnitQuantity}
                            onChange={(event) =>
                              updateVendorReturnLine(line.key, {
                                subUnitQuantity: event.target.value,
                              })
                            }
                          />
                        </td>
                        <td>
                          <input disabled value={selected?.subUnitName ?? ""} />
                        </td>
                        <td>
                          <div className="store-row-actions">
                            {vendorReturnLines.length > 1 ? (
                              <button
                                className="store-remove"
                                type="button"
                                onClick={() => removeVendorReturnLine(line.key)}
                                aria-label="Remove vendor return item"
                              >
                                x
                              </button>
                            ) : null}
                            {isLast && canAddMore ? (
                              <button
                                className="store-add"
                                type="button"
                                onClick={addVendorReturnLine}
                                aria-label="Add vendor return item"
                              >
                                +
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
          {kind === "store" &&
          returnItemsFound &&
          !returnableIssueLines.isLoading &&
          returnableIssueLines.data?.length === 0 ? (
            <EmptyState
              title="No returnable items found"
              message={
                returnMode === "serial"
                  ? "This department has no issued serial-number items available for return."
                  : "This department has no issued quantity available for return."
              }
            />
          ) : null}
          {kind === "vendor" &&
          vendorItemsFound &&
          !vendorReturnBatches.isLoading &&
          vendorReturnBatches.data?.length === 0 ? (
            <EmptyState
              title="No vendor items found"
              message="This vendor has no available stock batch to return."
            />
          ) : null}
          {kind !== "store" && kind !== "vendor" ? (
            <div className="store-totals-list">
              <div>
                <span>Return Value</span>
                <strong>Rs {returnAmount.toFixed(2)}</strong>
              </div>
            </div>
          ) : null}
          <div className="form-actions">
            <button
              className="button button-primary"
              disabled={saveDisabled}
              type="submit"
            >
              {createReturn.isPending || completeRepair.isPending
                ? "Saving..."
                : kind === "store"
                  ? "Save"
                  : kind === "vendor"
                    ? "Save & Approved"
                    : meta.submit}
            </button>
          </div>
        </form>
      ) : null}

      {kind === "repair" && showRepairForm ? (
        <form
          className="form-card compact-form"
          onSubmit={(event) => void finishRepair(event)}
        >
          <div className="section-heading">
            <h2>Complete repair and return to stock</h2>
            <button
              className="button button-secondary"
              type="button"
              onClick={() => {
                setRepairForm({
                  repairId: "",
                  repairFrom: "",
                  repairTimeDays: "0",
                  repairAmount: "0",
                  repairBillReference: "",
                });
                setShowRepairForm(false);
              }}
            >
              Cancel
            </button>
          </div>
          <div className="form-grid">
            <label className="field field-lg">
              Repair record
              <SearchableSelect
                value={repairForm.repairId}
                placeholder="Select approved pending repair"
                clearable={false}
                options={(returns.data?.data ?? [])
                  .filter(
                    (row) =>
                      row.status === "approved" &&
                      row.repairStatus === "pending",
                  )
                  .map((row) => ({
                    value: String(row.id),
                    label: `${text(row.returnNumber)} - ${text(row.returnDate)}`,
                  }))}
                onChange={(value) =>
                  setRepairForm((current) => ({ ...current, repairId: value }))
                }
              />
            </label>
            <label className="field">
              Repair From
              <input
                required
                value={repairForm.repairFrom}
                onChange={(event) =>
                  setRepairForm((current) => ({
                    ...current,
                    repairFrom: event.target.value,
                  }))
                }
              />
            </label>
            <label className="field field-xs">
              Repair Time (Days)
              <input
                type="number"
                min="0"
                required
                value={repairForm.repairTimeDays}
                onChange={(event) =>
                  setRepairForm((current) => ({
                    ...current,
                    repairTimeDays: event.target.value,
                  }))
                }
              />
            </label>
            <label className="field field-sm">
              Total Amount
              <input
                type="number"
                min="0"
                step="0.01"
                required
                value={repairForm.repairAmount}
                onChange={(event) =>
                  setRepairForm((current) => ({
                    ...current,
                    repairAmount: event.target.value,
                  }))
                }
              />
            </label>
            <label className="field">
              Bill / Reference
              <input
                value={repairForm.repairBillReference}
                onChange={(event) =>
                  setRepairForm((current) => ({
                    ...current,
                    repairBillReference: event.target.value,
                  }))
                }
              />
            </label>
          </div>
          <div className="form-actions">
            <button
              className="button button-primary"
              type="submit"
              disabled={completeRepair.isPending}
            >
              {completeRepair.isPending ? "Completing..." : "Complete repair"}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
};

type FieldSize = "xs" | "sm" | "lg" | "full";
const sizeClass = (size?: FieldSize) => (size ? ` field-${size}` : "");
const Lookup = ({
  label,
  value,
  options,
  onChange,
  size,
}: {
  label: string;
  value: string;
  options: { id: string; label: string }[] | undefined;
  onChange: (value: string) => void;
  size?: FieldSize;
}) => (
  <label className={`field${sizeClass(size)}`}>
    {label}
    <SearchableSelect
      value={value}
      options={(options ?? []).map((option) => ({
        value: option.id,
        label: option.label,
      }))}
      placeholder={`Select ${label}`}
      clearable={false}
      onChange={onChange}
    />
  </label>
);
